import { useCallback, useMemo, useState } from 'react';

import {
  useCreateSenderBlock,
  useDeleteSenderBlock,
  useSenders as useSenderAggregates,
} from '@hooks/api';
import type { ApiError } from '@services/http/client';
import type { SenderAggregate } from '@app-types/index';

import { displayFromEmail } from '../utils';

// One card per distinct sender, rolled up across every alias they've mailed.
export interface Sender {
  senderEmail: string;
  display: string;
  messageCount: number;
  aliasCount: number;
  lastReceivedAt: string | null;
  isBlocked: boolean;
  // The underlying per-(sender, alias) rows — block/unblock fan out over these.
  rows: SenderAggregate[];
}

export interface UseSendersReturn {
  active: Sender[];
  blocked: Sender[];
  total: number;
  blockedCount: number;
  isLoading: boolean;
  isError: boolean;
  error: ApiError | null;
  refetch: () => void;
  isEmpty: boolean;
  // Per-sender mutations (keyed by senderEmail so a row can show a spinner).
  block: (sender: Sender) => Promise<void>;
  unblock: (sender: Sender) => Promise<void>;
  mutatingEmail: string | null;
  // Sidebar "senders" badge — the live blocked-sender total (kept for MainLayout).
  senderBadgeCount: number;
}

// Roll the flat (sender, alias) aggregation rows up into one Sender per address.
function rollup(rows: SenderAggregate[]): Sender[] {
  const byEmail = new Map<string, Sender>();
  for (const row of rows) {
    const existing = byEmail.get(row.senderEmail);
    if (existing) {
      existing.messageCount += row.messageCount;
      existing.aliasCount += 1;
      existing.isBlocked = existing.isBlocked || row.isBlocked;
      if (
        row.lastReceivedAt &&
        (!existing.lastReceivedAt || row.lastReceivedAt > existing.lastReceivedAt)
      ) {
        existing.lastReceivedAt = row.lastReceivedAt;
      }
      existing.rows.push(row);
    } else {
      byEmail.set(row.senderEmail, {
        senderEmail: row.senderEmail,
        display: displayFromEmail(row.senderEmail),
        messageCount: row.messageCount,
        aliasCount: 1,
        lastReceivedAt: row.lastReceivedAt,
        isBlocked: row.isBlocked,
        rows: [row],
      });
    }
  }
  return [...byEmail.values()].sort(
    (a, b) => (b.lastReceivedAt ?? '').localeCompare(a.lastReceivedAt ?? ''),
  );
}

export function useSenders(): UseSendersReturn {
  // Pull a generous page — aggregation is grouped client-side over the rows.
  const query = useSenderAggregates({ pageSize: 200 });
  const createMutation = useCreateSenderBlock();
  const deleteMutation = useDeleteSenderBlock();
  const [mutatingEmail, setMutatingEmail] = useState<string | null>(null);

  const rows = useMemo(() => query.data?.results ?? [], [query.data]);
  const senders = useMemo(() => rollup(rows), [rows]);

  const active = useMemo(() => senders.filter((s) => !s.isBlocked), [senders]);
  const blocked = useMemo(() => senders.filter((s) => s.isBlocked), [senders]);

  // Block every alias row for this sender that isn't already blocked.
  const block = useCallback(
    async (sender: Sender) => {
      setMutatingEmail(sender.senderEmail);
      try {
        await Promise.all(
          sender.rows
            .filter((row) => !row.blockId)
            .map((row) =>
              createMutation.mutateAsync({ alias: row.aliasId, senderEmail: row.senderEmail }),
            ),
        );
      } finally {
        setMutatingEmail(null);
      }
    },
    [createMutation],
  );

  // Remove every existing block for this sender.
  const unblock = useCallback(
    async (sender: Sender) => {
      setMutatingEmail(sender.senderEmail);
      try {
        await Promise.all(
          sender.rows
            .filter((row): row is SenderAggregate & { blockId: string } => Boolean(row.blockId))
            .map((row) => deleteMutation.mutateAsync(row.blockId)),
        );
      } finally {
        setMutatingEmail(null);
      }
    },
    [deleteMutation],
  );

  const total = senders.length;
  const blockedCount = blocked.length;

  return {
    active,
    blocked,
    total,
    blockedCount,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
    refetch: query.refetch,
    isEmpty: !query.isLoading && !query.isError && total === 0,
    block,
    unblock,
    mutatingEmail,
    senderBadgeCount: blockedCount,
  };
}
